import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Truck, Lock, Tags, Download, Printer, ScanLine, Calendar, RotateCw, FileUp } from "lucide-react";

const POSTAGE_COLORS = {
  "Speed Post": "border-sky-200 bg-sky-50 text-sky-700",
  "Registered Periodical": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Registered Post": "border-indigo-200 bg-indigo-50 text-[#002FA7]",
  "Book Post": "border-slate-200 bg-slate-50 text-slate-700",
};
const STATUS_COLORS = {
  dispatched: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rto: "border-rose-200 bg-rose-50 text-rose-700",
  redispatched: "border-amber-200 bg-amber-50 text-amber-800",
  printed: "border-sky-200 bg-sky-50 text-sky-700",
};

export default function DispatchHome() {
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pers, setPers] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [pubId, setPubId] = useState("");
  const [perId, setPerId] = useState("");
  const today = new Date();
  const [issue, setIssue] = useState(`${String(today.getMonth() + 1).padStart(2, "0")}-${today.getFullYear()}`);
  const [data, setData] = useState(null);
  const [bundles, setBundles] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkPostage, setBulkPostage] = useState("Registered Periodical");
  const [locking, setLocking] = useState(false);

  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch {} })(); }, []);
  useEffect(() => {
    setPubId(""); setPerId(""); setPubs([]); setPers([]); setData(null); setSelected(new Set());
    if (!orgId) return;
    (async () => { try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } }); setPubs(data); } catch {} })();
  }, [orgId]);
  useEffect(() => {
    setPerId(""); setPers([]); setData(null); setSelected(new Set());
    if (!pubId) return;
    (async () => { try { const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId, page: 1, page_size: 100 } }); setPers(data.items.filter((x) => x.status === "active")); } catch {} })();
  }, [pubId]);

  const load = async () => {
    setData(null); setSelected(new Set()); setBundles(null);
    try {
      const [{ data }, { data: b }] = await Promise.all([
        api.get("/dispatch/eligible", { params: { periodical_id: perId, issue_month_year: issue } }),
        api.get("/dispatch/eligible-bundles", { params: { periodical_id: perId, issue_month_year: issue } }).catch(() => ({ data: null })),
      ]);
      setData(data);
      setBundles(b);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); setData({ items: [], total: 0 }); }
  };
  useEffect(() => { if (perId && /^\d{2}-\d{4}$/.test(issue)) load(); /* eslint-disable-next-line */ }, [perId, issue]);

  const toggleAll = () => {
    if (!data) return;
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map((x) => x.subscriber_id)));
  };
  const toggleOne = (id) => {
    const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n);
  };
  const selectedArr = useMemo(() => Array.from(selected), [selected]);

  const applyBulkPostage = async () => {
    if (selectedArr.length === 0) { toast.error("Select at least one subscriber"); return; }
    try {
      await api.patch("/dispatch/bulk-postage", { subscriber_ids: selectedArr, postage_class: bulkPostage });
      toast.success(`Postage updated for ${selectedArr.length} subscriber${selectedArr.length !== 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const lockDispatch = async () => {
    const ids = data?.items.filter((x) => !x.dispatch_id).map((x) => x.subscriber_id) || [];
    if (ids.length === 0) { toast.error("Nothing left to lock"); return; }
    setLocking(true);
    try {
      const { data: res } = await api.post("/dispatch/lock", { periodical_id: perId, issue_month_year: issue, subscriber_ids: ids });
      toast.success(`Dispatch locked · ${res.locked} new label${res.locked !== 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLocking(false); }
  };

  const downloadManifest = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE}/dispatch/manifest.csv?periodical_id=${perId}&issue_month_year=${issue}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error("Failed to download manifest"); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `manifest-${issue}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
  };

  const lockedCount = data?.items.filter((x) => x.dispatch_id).length || 0;
  const pendingCount = (data?.items.length || 0) - lockedCount;

  return (
    <div data-testid="dispatch-home" className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Module 07 · Dispatch & Logistics</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Dispatch</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Generate the issue dispatch list (eligibility = active or grace covering the issue month). Pincode-sorted for India Post bundling.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="dispatch-go-labels" className="rounded-sm"><Link to="/dispatch/labels"><Printer size={14} className="mr-1.5" />Labels</Link></Button>
          <Button variant="outline" asChild data-testid="dispatch-go-rto" className="rounded-sm"><Link to="/dispatch/rto"><ScanLine size={14} className="mr-1.5" />RTO Scan</Link></Button>
          <Button variant="outline" asChild data-testid="dispatch-go-redispatch" className="rounded-sm"><Link to="/dispatch/redispatch-queue"><RotateCw size={14} className="mr-1.5" />Re-dispatch</Link></Button>
          <Button variant="outline" asChild data-testid="dispatch-go-import" className="rounded-sm"><Link to="/dispatch/tracking-import"><FileUp size={14} className="mr-1.5" />Import Tracking</Link></Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-sm border border-slate-200 bg-white p-4 sm:grid-cols-4">
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</Label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger data-testid="disp-org-select" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Publication House</Label>
          <Select value={pubId} onValueChange={setPubId} disabled={!orgId}>
            <SelectTrigger data-testid="disp-pub-select" className="rounded-sm"><SelectValue placeholder={orgId ? "Select" : "—"} /></SelectTrigger>
            <SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Periodical <span className="text-rose-600">*</span></Label>
          <Select value={perId} onValueChange={setPerId} disabled={!pubId}>
            <SelectTrigger data-testid="disp-per-select" className="rounded-sm"><SelectValue placeholder={pubId ? "Select" : "—"} /></SelectTrigger>
            <SelectContent>{pers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Issue (MM-YYYY) <span className="text-rose-600">*</span></Label>
          <div className="relative">
            <Calendar size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="disp-issue-input" value={issue} onChange={(e) => setIssue(e.target.value)} placeholder="06-2024" className="rounded-sm pl-9 font-mono" />
          </div>
        </div>
      </div>

      {!perId ? (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          <Truck className="mx-auto mb-3 text-slate-400" size={28} />
          Select a periodical to generate the issue dispatch list.
        </div>
      ) : (
        <>
          {/* Pincode bundle summary (server-side aggregation; only includes locked dispatch records) */}
          {bundles && bundles.bundle_count > 0 && (
            <div className="mb-3 rounded-sm border border-slate-200 bg-white p-4" data-testid="bundle-summary">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Pincode Bundles · Locked</div>
                <div className="text-xs text-slate-500">India Post bundle counters · sorted by PIN</div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-sm border border-slate-200 p-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500" title="Total mail pieces locked for this issue">Total</div>
                  <div className="font-mono text-lg font-semibold text-slate-900">{bundles.total}</div>
                  <div className="text-[9px] text-slate-400">mail pieces</div>
                </div>
                <div className="rounded-sm border border-slate-200 p-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500" title="Number of unique pincodes — one tied bundle per pincode for India Post">Bundles</div>
                  <div className="font-mono text-lg font-semibold text-[#002FA7]">{bundles.bundle_count}</div>
                  <div className="text-[9px] text-slate-400">unique pincodes</div>
                </div>
                <div className="rounded-sm border border-slate-200 p-2.5 col-span-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Postage Mix</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(bundles.by_postage_total || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span key={k} className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-medium ${POSTAGE_COLORS[k] || "border-slate-200 bg-slate-50 text-slate-700"}`}>{k} · {v}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="bundle-pin-strip">
                {bundles.bundles.slice(0, 30).map((b) => (
                  <span key={b.pincode} title={`${b.city || ""}`} className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px]">
                    <span className="font-mono font-semibold text-slate-900">{b.pincode}</span>
                    <span className="ml-1 text-slate-500">×{b.count}</span>
                  </span>
                ))}
                {bundles.bundles.length > 30 && <span className="text-[11px] text-slate-500">+{bundles.bundles.length - 30} more bundles…</span>}
              </div>
            </div>
          )}

          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-sm border border-slate-200 bg-white p-3">
            <span className="text-sm text-slate-600 mr-2">{data ? <><span className="font-mono font-semibold text-slate-900">{data.total}</span> eligible · <span className="font-mono">{lockedCount}</span> locked · <span className="font-mono">{pendingCount}</span> pending</> : "Loading…"}</span>
            <div className="ml-auto flex flex-wrap gap-2">
              <Select value={bulkPostage} onValueChange={setBulkPostage}>
                <SelectTrigger className="w-[210px] rounded-sm" data-testid="disp-bulk-postage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(POSTAGE_COLORS).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={applyBulkPostage} data-testid="disp-bulk-apply" className="rounded-sm" disabled={selected.size === 0}>
                <Tags size={14} className="mr-1.5" />Apply to {selected.size}
              </Button>
              <Button variant="outline" onClick={downloadManifest} data-testid="disp-manifest-btn" className="rounded-sm">
                <Download size={14} className="mr-1.5" />Manifest CSV
              </Button>
              <Button onClick={lockDispatch} disabled={locking || pendingCount === 0} data-testid="disp-lock-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
                <Lock size={14} className="mr-1.5" />{locking ? "Locking…" : `Lock dispatch (${pendingCount})`}
              </Button>
            </div>
          </div>

          <div className="rounded-sm border border-slate-200 bg-white">
            <Table data-testid="disp-table" className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"><Checkbox checked={!!data && selected.size === data.items.length && data.items.length > 0} onCheckedChange={toggleAll} /></TableHead>
                  <TableHead>Pin · City</TableHead>
                  <TableHead>Subscriber</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Postage</TableHead>
                  <TableHead>QR / Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data === null && [...Array(5)].map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
                {data && data.items.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500">No eligible subscribers for this issue.</TableCell></TableRow>}
                {data?.items.map((it) => (
                  <TableRow key={it.subscriber_id} data-testid={`disp-row-${it.subscriber_id}`}>
                    <TableCell><Checkbox checked={selected.has(it.subscriber_id)} onCheckedChange={() => toggleOne(it.subscriber_id)} data-testid={`disp-check-${it.subscriber_id}`} /></TableCell>
                    <TableCell className="font-mono text-sm"><span className="font-semibold">{it.pincode}</span><div className="text-xs text-slate-500">{it.city}</div></TableCell>
                    <TableCell><div className="font-medium text-slate-900">{it.display_name}</div><div className="font-mono text-[11px] text-slate-500">{it.subscriber_code} · {it.mobile_no}</div></TableCell>
                    <TableCell className="text-xs text-slate-600">{it.address_line_1}{it.address_line_2 ? `, ${it.address_line_2}` : ""}{it.landmark ? ` · ${it.landmark}` : ""}</TableCell>
                    <TableCell><span className={`rounded-sm border px-2 py-0.5 text-xs font-medium ${POSTAGE_COLORS[it.postage_class] || ""}`}>{it.postage_class}</span></TableCell>
                    <TableCell>
                      {it.dispatch_id ? (
                        <div className="flex flex-col gap-1">
                          <span className="font-mono text-xs text-slate-700">{it.qr_code_string}</span>
                          <span className={`w-fit rounded-sm border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${STATUS_COLORS[it.dispatch_status] || ""}`}>{it.dispatch_status}</span>
                        </div>
                      ) : <span className="text-xs text-slate-400">— pending —</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
